/**
 * ContextWorkspace — the reusable stacked-layout workspace.
 *
 * GC65 Phase C · Founder direction (verbatim):
 *   "Build one reusable contextual-workspace framework driven by
 *    entity configuration. Mobile-first. The workspace must answer
 *    three questions immediately: What am I looking at? What has
 *    happened? What is this connected to?"
 *
 * Sections in order:
 *   1. Identity — entity name/label/status/date, one-tap back
 *   2. Timeline — chronological history for THIS entity, mixed types
 *   3. Related — cards grouped by linked entity type, tap-in navigable
 *
 * Two-way navigation is mandatory (Founder D(a)) — every related
 * card links to the linked entity's OWN /context/:type/:id workspace.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, Link2, Lock } from "lucide-react";
import "./ContextWorkspace.css";

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };

const TYPE_LABELS = {
  upload: "Upload", booking: "Booking", lesson: "Lesson", pupil: "Pupil",
  vehicle: "Vehicle", instructor: "Instructor", ai_analysis: "AI Analysis",
  business_record: "Business Record", document: "Document", receipt: "Receipt",
  voice_note: "Voice Note", reflection: "Reflection", mileage: "Mileage",
  fuel: "Fuel", bank_statement: "Bank Statement",
};

// Human relationship labels per Founder direction — no DB terms.
const KIND_LABELS = {
  lesson_of_pupil: "Lesson for",
  pupil_of_lesson: "Lesson for",
  upload_of_lesson: "Uploaded during",
  lesson_of_upload: "Uploaded during",
  ai_analysis_of_upload: "Analysed from",
  upload_of_ai_analysis: "Analysed from",
  booking_of_lesson: "Booked as",
  lesson_of_booking: "Booked as",
};
const humanKind = (k) => KIND_LABELS[k] || k.replace(/_/g, " ");

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ContextWorkspace() {
  const { entityType, entityId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    setErr(null); setData(null);
    try {
      const r = await axios.get(
        `${API}/api/context/${entityType}/${entityId}`, OPTS,
      );
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.status === 404
        ? "This record is unavailable — it may have been removed or you don't have access."
        : (e?.response?.data?.detail || "Couldn't load this workspace."));
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <main className="ctx" data-testid="ctx-error-page">
        <button type="button" onClick={() => nav(-1)} className="ctx-back" data-testid="ctx-back">
          <ArrowLeft aria-hidden="true" /> Back
        </button>
        <div className="ctx-alert" role="alert">{err}</div>
      </main>
    );
  }

  if (!data) {
    return <main className="ctx" data-testid="ctx-loading"><p>Loading…</p></main>;
  }

  const { entity, timeline, related } = data;
  const label = TYPE_LABELS[entityType] || entityType;
  const title = entity?.meta?.filename || entity?.meta?.title || entity?.entity_id;

  const relatedTypes = Object.keys(related?.by_type || {});
  const showTimeline = tab === "all" || tab === "timeline";
  const showRelated  = tab === "all" || tab === "related";

  return (
    <main className="ctx" data-testid={`ctx-page-${entityType}-${entityId}`}>
      {/* 1 · IDENTITY */}
      <button type="button" onClick={() => nav(-1)} className="ctx-back" data-testid="ctx-back">
        <ArrowLeft aria-hidden="true" /> Back
      </button>
      <header className="ctx-identity" data-testid="ctx-identity">
        <span className="ctx-kind">{label}</span>
        <h1 className="ctx-title">{title}</h1>
        <p className="ctx-meta">
          {fmt(entity.created_at)}
          {entity.status && <> · {entity.status}</>}
          {entity.is_backfill && <> · <em>backfilled</em></>}
        </p>
      </header>

      <nav className="ctx-tabs" data-testid="ctx-tabs">
        <button type="button" onClick={() => setTab("all")}      className={tab === "all" ? "is-active" : ""} data-testid="ctx-tab-all">All</button>
        <button type="button" onClick={() => setTab("timeline")} className={tab === "timeline" ? "is-active" : ""} data-testid="ctx-tab-timeline">Timeline ({timeline.total})</button>
        <button type="button" onClick={() => setTab("related")}  className={tab === "related" ? "is-active" : ""} data-testid="ctx-tab-related">Related ({related.total})</button>
      </nav>

      {/* 2 · TIMELINE */}
      {showTimeline && (
        <section className="ctx-section" data-testid="ctx-timeline">
          <h2><Clock aria-hidden="true" /> What has happened</h2>
          {timeline.items.length === 0 ? (
            <p className="ctx-empty">Nothing has happened here yet. Actions will appear as they occur.</p>
          ) : (
            <ol className="ctx-timeline">
              {timeline.items.map((r) => (
                <li key={r.id} data-testid={`ctx-timeline-item-${r.id}`}>
                  <strong>{r.action}</strong>
                  <span>{fmt(r.created_at)}</span>
                  {r.source && <em>{r.source}</em>}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {/* 3 · RELATED */}
      {showRelated && (
        <section className="ctx-section" data-testid="ctx-related">
          <h2><Link2 aria-hidden="true" /> What this is connected to</h2>
          {relatedTypes.length === 0 ? (
            <p className="ctx-empty">
              No connections yet. Records become connected automatically when they share a lesson,
              pupil, booking, upload or AI analysis.
            </p>
          ) : relatedTypes.map((rt) => (
            <div key={rt} className="ctx-related-group" data-testid={`ctx-related-group-${rt}`}>
              <h3>{TYPE_LABELS[rt] || rt} ({related.by_type[rt].length})</h3>
              <ul>
                {related.by_type[rt].map((card) => (
                  <li key={card.relationship_id} className={card.accessible ? "" : "is-locked"} data-testid={`ctx-related-card-${card.relationship_id}`}>
                    {card.accessible ? (
                      <Link to={`/context/${card.other_type}/${card.other_id}`}>
                        <span className="ctx-related-kind">{humanKind(card.kind)}</span>
                        <strong>{card.summary?.meta?.filename || card.summary?.meta?.title || card.other_id}</strong>
                        {card.summary?.created_at && <span>{fmt(card.summary.created_at)}</span>}
                      </Link>
                    ) : (
                      <div className="ctx-locked" title="You don't have access to this linked record">
                        <Lock aria-hidden="true" />
                        <span>Restricted record</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
    </main>
  );
}
